/**
 * Order API
 *
 * API endpoints for order management operations
 */

import dayjs from "dayjs";
import toast from "react-hot-toast";

import { nextCargoApi } from "../../baseApi";
import type {
  AddOrderPayload,
  GetOrderHasDriverArgs,
  GetOrderHasDriverResponse,
  ListOrderData,
  ListOrdersResponse,
  Order,
  OrderSearch,
  SaveOrderPayload,
  TransformedListOrdersResponse
} from "./order.types";
import { getOrderHasDriverArgsSchema } from "./schemas/getOrderHasDriver";

/**
 * Order API endpoints
 * Provides CRUD operations for order management
 */
export const orderApi = nextCargoApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Get all orders
     * @returns Array of orders
     */
    getOrders: build.query<TransformedListOrdersResponse["data"], { filter?: string; company: number }, TransformedListOrdersResponse>({
      query: (params) => ({
        url: "/order",
        params
      }),
      transformResponse: (response) => {
        const transformedData = response.data.map((order) => ({
          ...order,
          code: order.code ? order.code : "N/A"
        }));

        return transformedData;
      },
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando pedidos...",
          success: "Pedidos carregados com sucesso!",
          error: "Erro ao carregar pedidos!"
        });
      }
    }),

    /**
     * List orders with pagination (infinite query)
     * @param id - Company ID
     * @param params - Filter parameters
     * @returns Paginated list of orders
     */
    listOrders: build.infiniteQuery<
      TransformedListOrdersResponse,
      { id: number; params?: App.Filters.Base<ListOrderData> },
      App.Filters.Pagination<ListOrderData>,
      ListOrdersResponse
    >({
      infiniteQueryOptions: {
        initialPageParam: { page: 0, pageSize: 8, sortDirection: "desc", sortField: "createAt" },
        getNextPageParam: (lastPage, _, lastPageParam) => {
          const nextPage = lastPageParam.page + 1;
          const remainingPages = lastPage?.total - nextPage;

          if (remainingPages <= 0) {
            return undefined;
          }

          return {
            ...lastPageParam,
            page: nextPage
          };
        },
        getPreviousPageParam: (_, __, firstPageParam) => {
          const prevPage = firstPageParam.page - 1;
          if (prevPage < 0) return undefined;

          return {
            ...firstPageParam,
            page: prevPage
          };
        }
      },
      query: ({ queryArg: { params }, pageParam }) => ({
        url: `/order`,
        params: Object.assign({}, pageParam, params)
      }),
      transformResponse: (response) => {
        // const _total = response.data.reduce((acc, item) => acc + (item.qtyOrdered - item.qtyTransported), 0);
        const transformedOrders = response.data.map((order) => ({
          ...order,
          code: order.code ? order.code : "N/A",
          createAt: dayjs(order.createAt).format("DD/MM/YYYY"),
          qtyOrdered: order.qtyOrdered.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          qtyTransported: order.qtyTransported?.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
          status: order.status ? `${order.status?.toFixed(2)}%` : "0.00%",
          total: 0
        }));

        return { ...response, data: transformedOrders };
      },
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Carregando pedidos...",
          success: "Pedidos carregados com sucesso!",
          error: "Erro ao carregar pedidos!"
        });
      }
    }),

    /**
     * Check if an order has a specific driver
     * @param orderId - Order ID
     * @param driverId - Driver ID
     * @returns Whether the driver is associated with the order
     */
    getOrderHasDriver: build.query<GetOrderHasDriverResponse, GetOrderHasDriverArgs>({
      query: ({ orderId, driverId }) => `/order/${orderId}/hasDriver/${driverId}`,
      argSchema: getOrderHasDriverArgsSchema,
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Verificando se este motorista já está atrelado a um pedido...",
          success: "Verificado com sucesso!",
          error: "Um erro ocorreu na verificação de existencia do motorista na fila. Efetue uma nova tentativa ou tente novamente mais tarde!"
        });
      }
    }),

    /**
     * Add a new order
     * @param payload - Order data
     * @returns Created order
     */
    createOrder: build.mutation<Order, AddOrderPayload>({
      query: (body) => ({
        url: "/order",
        method: "POST",
        body
      }),
      onQueryStarted: async (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Adicionando...",
          success: "Adicionado com sucesso!",
          error: "Erro ao adicionar o pedido"
        });
      }
    }),

    /**
     * Save an order (create or update)
     * @param payload - Order data with id (null for create, number for update)
     * @returns Saved order
     */
    saveOrder: build.mutation<Order, SaveOrderPayload>({
      query: (body) => ({
        url: "/order/save",
        method: "POST",
        body
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Salvando...",
          success: "Salvo com sucesso!",
          error: "Erro ao salvar o pedido"
        });
      }
    }),

    /**
     * Delete an order
     * @param id - Order ID to delete
     * @returns void
     */
    deleteOrder: build.mutation<void, Pick<Order, "id">>({
      query: ({ id }) => ({
        url: `/order/delete/${id}`,
        method: "DELETE"
      }),
      onQueryStarted: (args, { queryFulfilled }) => {
        toast.promise(queryFulfilled, {
          loading: "Deletando...",
          success: "Deletado com sucesso!",
          error: "Erro ao deletar o pedido"
        });
      }
    }),

    /**
     * Search orders by query string
     * @param order - Search query
     * @returns Array of matching orders (minimal fields)
     */
    searchOrders: build.query<OrderSearch[], Pick<OrderSearch, "order">>({
      query: ({ order }) => ({
        url: `/order/like/${order}`
      })
    })
  })
});

/**
 * Enhance endpoints with cache tag configuration
 */
orderApi.enhanceEndpoints({
  addTagTypes: ["Orders", "SearchOrders", "OrderHasDriver"],
  endpoints: {
    getOrders: {
      providesTags: (result) => (result ? [...result.map(({ id }) => ({ type: "Orders" as const, id })), { type: "Orders", id: "LIST" }] : [{ type: "Orders", id: "LIST" }])
    },
    listOrders: {
      providesTags: (result) => (result ? [...result.pages.flatMap(({ data }) => data.map(({ id }) => ({ type: "ListOrder" as const, id })))] : [{ type: "ListOrder", id: "LIST" }])
    },
    getOrderHasDriver: {
      providesTags: (result, err, { orderId }) => [{ type: "OrderHasDriver", id: orderId }]
    },
    createOrder: {
      invalidatesTags: (result, err, args) => {
        const id = (args as Partial<Order>).id;
        return !err ? (id ? [{ type: "Orders", id }] : [{ type: "Orders", id: "LIST" }]) : [];
      }
    },
    saveOrder: {
      invalidatesTags: (result, err, { id }) => (!err ? (id ? [{ type: "Orders", id }] : [{ type: "Orders", id: "LIST" }]) : [])
    },
    deleteOrder: {
      invalidatesTags: (result, err, { id }) =>
        !err
          ? [
              { type: "Orders", id },
              { type: "Orders", id: "LIST" }
            ]
          : []
    },
    searchOrders: {
      providesTags: (result) =>
        result ? [...result.map(({ id }) => ({ type: "SearchOrders" as const, id })), { type: "SearchOrders", id: "LIST" }] : [{ type: "SearchOrders", id: "LIST" }]
    }
  }
});
